import { Injectable } from '@angular/core';
import { CanActivate, CanActivateChild, CanMatch, Route, Router, UrlSegment, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { AuthService } from '../services/auth/auth.service';

@Injectable({ providedIn: 'root' })
export class OnboardingGuard implements CanMatch, CanActivate, CanActivateChild {
  constructor(private readonly auth: AuthService, private readonly router: Router) {}

  private check(target: string): Observable<boolean> {
    const loading$ = this.auth.isLoading$;
    const auth$ = this.auth.isAuthenticated$;
    const wiz$ = this.auth.isWizardCompleted();
    return combineLatest({ loading: loading$, isAuth: auth$, wizDone: wiz$ }).pipe(
      filter(({ loading }) => loading === false),
      take(1),
      map(({ loading, isAuth, wizDone }) => {
        const resolvedFlow = this.auth.getResolvedFlow();
        console.info('[OnboardingGuard] target=', target, 'loading=', loading, 'isAuth=', isAuth, 'wizardDone=', wizDone, 'resolvedFlow=', resolvedFlow);
        if (!isAuth) return true; // dejar que AuthGuard maneje auth
        return this.resolveAuthenticatedAccess(target, wizDone, resolvedFlow, this.auth.getPendingInviteTarget());
      })
    );
  }

  private resolveAuthenticatedAccess(
    target: string,
    wizDone: boolean,
    resolvedFlow: ReturnType<AuthService['getResolvedFlow']>,
    inviteTarget: string | null
  ): boolean {
    const clean = (target || '').split('?')[0];

    if (wizDone && resolvedFlow) {
      this.auth.clearResolvedFlow();
    }

    if (resolvedFlow === 'registered') {
      if (clean === '/wizard') {
        this.router.navigate(['/chat']);
        return false;
      }

      return true;
    }

    if (resolvedFlow === 'invited' && inviteTarget && clean !== '/onboarding') {
      this.router.navigateByUrl(inviteTarget);
      return false;
    }

    if (resolvedFlow === 'new_user' && clean !== '/wizard') {
      this.router.navigate(['/wizard']);
      return false;
    }

    if (!wizDone && inviteTarget && clean !== '/onboarding') {
      this.router.navigateByUrl(inviteTarget);
      return false;
    }

    if (!wizDone && clean !== '/wizard') {
      this.router.navigate(['/wizard']);
      return false;
    }

    if (wizDone && clean === '/wizard') {
      this.router.navigate(['/chat']);
      return false;
    }

    return true;
  }

  canMatch(route: Route, segments: UrlSegment[]): Observable<boolean> {
    const target = '/' + segments.map(s => s.path).join('/');
    return this.check(target);
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.check(state.url || '/');
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.check(state.url || '/');
  }
}

